import Tippy from '@tippyjs/react';
import { useTranslation } from 'next-i18next';
import { useState, VFC } from 'react';

import CustomButton from '@/components/CustomButton';
import Notice from '@/components/Notice';
import ProcessResultAlert from '@/components/ProcessResultAlert';
import { GraphToken } from '@/components/SwapWithIPFS/types';
import generateSquareImageURL from '@/utils/generateSquareImageURL';
import { convertImgixURL } from '@/utils/imgixUtils';

import styles from './SwapTokenConfirmation.module.scss';
import QuestionMarkIcon from './question-mark-icon.svg';

type Props = {
  userTokens: {
    __typename: string;
    id: string;
    token: {
      __typename: string;
      id: string;
      creationTime: string;
      tokenId: string;
      totalTokenAmount: string;
      creatorName: string;
      description: string;
      imageUrl: string;
      tokenName: string;
    };
    tokenAmount: string;
  }[];
  onSwapButtonClick(selectedTokenIDs: GraphToken[]): void;
  isProcessing: boolean;
  errorMessage: string | null;
};

const SwapTokenConfirmation: VFC<Props> = ({
  userTokens,
  onSwapButtonClick,
  isProcessing,
  errorMessage,
}: Props) => {
  const { t } = useTranslation();

  const [selectedTokens, setSelectedTokens] = useState<GraphToken[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSwapButtonClick = () => {
    if (userTokens.length <= 10) {
      // Tell the parent component as if all tokens were selected
      onSwapButtonClick(userTokens);
      return;
    }

    if (!selectedTokens.length) {
      // No tokens selected
      return;
    }

    // Tell the parent component selected tokens
    onSwapButtonClick(selectedTokens);
  };

  // If there are more, show selection
  return (
    <div className={styles.swapTokenSelection}>
      {showConfirmation ? (
        <>
          <h3>
            {t('swapWithIPFS.confirmation.title', {
              count: selectedTokens.length,
            })}
          </h3>

          <div className={styles.selectedTokenGrid}>
            {selectedTokens.map((token) => (
              <Tippy content={token.token.tokenName} key={token.id}>
                <img
                  src={generateSquareImageURL(
                    convertImgixURL(token.token.imageUrl),
                    200
                  )}
                  alt={`Token image of ${token.token.tokenName}`}
                />
              </Tippy>
            ))}
          </div>

          <p>{t('swapWithIPFS.modalContent.description')}</p>

          <Notice>
            <span>{t('swapWithIPFS.modalContent.note')}: </span>
            {t('swapWithIPFS.modalContent.gasFeeNotice')}
          </Notice>

          <div className={styles.confirmationButtonContainer}>
            <CustomButton
              variant="primaryOutlined"
              onClick={() => setShowConfirmation(false)}
              loading={isProcessing}
            >
              {t('back')}
            </CustomButton>

            <CustomButton
              variant="primary"
              onClick={handleSwapButtonClick}
              loading={isProcessing}
            >
              {t('yes')}
            </CustomButton>
          </div>

          {errorMessage && (
            <div style={{ marginTop: '20px' }}>
              <ProcessResultAlert status="fail" failMessage={errorMessage} />
            </div>
          )}
        </>
      ) : (
        <>
          <h3>
            {t('swapWithIPFS.swapWithIPFS')}
            <Tippy
              content={t('swapWithIPFS.confirmation.description')}
              arrow={false}
              hideOnClick={false}
              placement="bottom"
              className={styles.descriptionTippy}
            >
              <button type="button">
                <QuestionMarkIcon />
              </button>
            </Tippy>
          </h3>
          <p className={styles.chooseTokenDescription}>
            {t('swapWithIPFS.confirmation.chooseTokenDescription')}
          </p>

          <ul className={styles.tokenSelectionList}>
            {userTokens.map((userToken) => (
              <li key={userToken.id}>
                <label htmlFor={`checkbox-token-${userToken.id}`}>
                  <input
                    type="checkbox"
                    id={`checkbox-token-${userToken.id}`}
                    checked={selectedTokens.includes(userToken)}
                    onChange={(e) => {
                      const selectedTokensSet = new Set(selectedTokens);
                      if (e.target.checked) {
                        selectedTokensSet.add(userToken);
                      } else {
                        selectedTokensSet.delete(userToken);
                      }
                      setSelectedTokens(Array.from(selectedTokensSet));
                    }}
                    disabled={
                      selectedTokens.length >= 10 &&
                      !selectedTokens.includes(userToken)
                    }
                  />

                  <span>
                    {parseInt(userToken.id) > 9 && (
                      // Temporary Fix, don't display LimitedCollections Images in Modal
                      <img
                        src={generateSquareImageURL(
                          convertImgixURL(userToken.token.imageUrl),
                          50
                        )}
                        alt={`Token image of ${userToken.token.tokenName}`}
                      />
                    )}
                    {userToken.token.tokenName}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <div className={styles.selectConfirmation}>
            <CustomButton
              onClick={() => setShowConfirmation(true)}
              disabled={
                selectedTokens.length <= 0 || selectedTokens.length > 10
              }
            >
              {t('swapWithIPFS.swapWithIPFS')} ({selectedTokens.length}/10)
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default SwapTokenConfirmation;
